import * as enums from '@local/finsera-core/src/enums';
import { _ } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

const rowHasAllValues = (row: types.widgets.TableData[0] | undefined) =>
  Object.values(row ?? {}).every((value) => value != null);

const getNewData = (
  data: types.widgets.TableData,
  selectedPeriod: enums.PERIOD_ENUM,
  xAxisField: string,
  forceSameEnd: boolean
) => {
  const periods = Object.values(enums.PERIOD_ENUM) as string[];
  const newData = data.map((row: types.widgets.TableData[0]) => {
    const newRow = {} as types.widgets.TableData[0];

    Object.keys(row).forEach((key: string) => {
      if (selectedPeriod === enums.PERIOD_ENUM.FULL) {
        // FULL can be represented in 2 ways:
        // 1. Keys that don't start with any period (original data without period prefix)
        // 2. Keys that start with "FULL_" prefix
        if (!periods.some((period) => key.startsWith(period)) || key.startsWith(enums.PERIOD_ENUM.FULL)) {
          const cleanKey = key.startsWith(enums.PERIOD_ENUM.FULL) ? key.slice(enums.PERIOD_ENUM.FULL.length + 1) : key;
          newRow[cleanKey] = row[key];
        }
      } else if (key.includes(selectedPeriod)) {
        newRow[key.slice(selectedPeriod.length + 1)] = row[key];
      }
      newRow[xAxisField] = row[xAxisField];
    });
    return newRow;
  });

  const seriesData = _.sortBy(newData, xAxisField);
  const dataKeys = _.without(Object.keys(seriesData?.[0] ?? {}), xAxisField);

  // Find first non-null data point across all datapoints
  const firstValidIndex = seriesData.findIndex((row) => _.some(dataKeys, (key) => !_.isNil(row?.[key])));
  if (firstValidIndex === -1) return [];

  if (!forceSameEnd) return seriesData.slice(firstValidIndex);

  // Last row where every series has a value (common end date)
  let lastValidIndex = -1;
  for (let i = seriesData.length - 1; i >= firstValidIndex; i--) {
    if (dataKeys.length > 0 && dataKeys.every((key) => !_.isNil(seriesData[i]?.[key]))) {
      lastValidIndex = i;
      break;
    }
  }

  if (lastValidIndex === -1) return [];
  return seriesData.slice(firstValidIndex, lastValidIndex + 1);
};

export const getPeriodData = ({
  data,
  xAxisField,
  forceSameStart = false,
  forceSameEnd = false,
}: {
  data: types.widgets.TableData;
  xAxisField: string;
  forceSameStart?: boolean;
  forceSameEnd?: boolean;
}) => {
  const availablePeriods = [] as enums.PERIOD_ENUM[];
  const allData = {} as Record<enums.PERIOD_ENUM, types.widgets.TableData>;

  Object.values(enums.PERIOD_ENUM).forEach((key) => {
    allData[key] = getNewData(data, key, xAxisField, forceSameEnd);
    const periodData = allData[key];
    if (_.isEmpty(periodData)) return;

    const startOk = !forceSameStart || rowHasAllValues(periodData[0]);
    const endOk = !forceSameEnd || rowHasAllValues(periodData.at(-1));
    if (startOk && endOk) availablePeriods.push(key);
  });

  return {
    allData,
    availablePeriods,
  };
};
